import { memo, useMemo, useRef } from 'react';
import type { ChapterEndData } from '../types';
import {
  DEFAULT_CHAPTER_END_CONTINUE_TEXT,
  resolveChapterEndPrimaryActionLabel,
  selectVisiblePerfRows,
} from '../systems/chapter-end-content';
import {
  PEDA_CATEGORY_LABELS,
  PEDA_CATEGORY_SHORT_LABELS,
  PEDA_CATEGORY_TILE_KEYS,
} from '../systems/peda-categories';
import {
  resolvePedaTileStatus,
  type PedaCategoryScoreRow,
  type PedaEndStats,
} from '../systems/peda-shield-stats';
import { formatAllyTrustSubtitle, type AllyTrustDisplayRow } from '../systems/ally-trust';
import { FormattedInlineText } from './formatted-inline-text';
import styles from './chapter-end-screen.module.css';

export interface ChapterConfidenceGainDisplay {
  characterId: string;
  name: string;
  portraitImage?: string;
  delta: number;
  current: number;
}

export interface ChapterEndScreenProps {
  data: ChapterEndData;
  chapterShieldsEarned?: number;
  chapterConfidenceGains?: ChapterConfidenceGainDisplay[];
  continueText?: string;
  evaluateCondition: (condition: string) => boolean;
  pedaEndStats?: PedaEndStats | null;
  allyTrustRows?: AllyTrustDisplayRow[];
  onContinue?: () => void;
  onRestart?: () => void;
  onMenu?: () => void;
  exiting?: boolean;
  /** Skip opaque overlay fade when the bridge already established the dark backdrop. */
  transparentOverlay?: boolean;
}

function emptyPedaTiles(): PedaCategoryScoreRow[] {
  return PEDA_CATEGORY_TILE_KEYS.map((key) => ({
    key,
    label: PEDA_CATEGORY_LABELS[key],
    shortLabel: PEDA_CATEGORY_SHORT_LABELS[key],
    earned: 0,
    max: 0,
  }));
}

function shieldNoun(count: number): string {
  return count === 1 ? 'bouclier' : 'boucliers';
}

export const ChapterEndScreen = memo(function ChapterEndScreen({
  data,
  continueText = DEFAULT_CHAPTER_END_CONTINUE_TEXT,
  evaluateCondition,
  pedaEndStats = null,
  allyTrustRows = [],
  onContinue,
  onRestart,
  onMenu,
  exiting = false,
  transparentOverlay = false,
}: ChapterEndScreenProps) {
  const visiblePerfRows = useMemo(
    () => selectVisiblePerfRows(data.perfRows, evaluateCondition),
    [data.perfRows, evaluateCondition],
  );
  const tiles = pedaEndStats?.categories?.length ? pedaEndStats.categories : emptyPedaTiles();
  const visibleAllyTrustRows = useMemo(
    () => allyTrustRows.filter((row) => row.points > 0),
    [allyTrustRows],
  );
  const allySummary = useMemo(
    () => formatAllyTrustSubtitle(visibleAllyTrustRows),
    [visibleAllyTrustRows],
  );
  const primaryLabel = resolveChapterEndPrimaryActionLabel(continueText);
  const ribbon = data.eyebrow?.trim() || data.missionLabel?.trim() || '';
  const rankTitle = pedaEndStats?.rank.label || data.heading;
  const percent = pedaEndStats?.rank.percent ?? 0;
  const earnedTotal = pedaEndStats?.earnedTotal ?? 0;
  const possibleTotal = pedaEndStats?.possibleTotal ?? 0;
  const showAllyTrust = visibleAllyTrustRows.length > 0;
  const showPerfRows = visiblePerfRows.length > 0;

  const entranceDoneRef = useRef(false);
  const showOverlayEntering = !exiting && !transparentOverlay && !entranceDoneRef.current;
  const showLayoutEntering = !exiting && !entranceDoneRef.current;

  return (
    <div
      className={`${styles.overlay} ${transparentOverlay ? styles.overlayTransparent : ''} ${exiting ? styles.overlayExiting : showOverlayEntering ? styles.overlayEntering : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chapter-end-heading"
    >
      <div
        className={`${styles.layout} ${exiting ? styles.layoutExiting : showLayoutEntering ? styles.layoutEntering : ''}`}
        onAnimationEnd={(event) => {
          if (event.animationName.includes('chapterEndCardIn')) {
            entranceDoneRef.current = true;
          }
        }}
      >
        <div className={styles.halo1} aria-hidden />
        <div className={styles.halo2} aria-hidden />
        <div className={styles.scanlines} aria-hidden />
        <div className={`${styles.col} cn-zoom-ui`}>
          <div className={styles.head}>
            <div className={styles.shieldWrap} aria-hidden>
              <svg viewBox="0 0 104 116" className={styles.shieldSvg}>
                <path
                  d="M52 3 L99 20 V63 c0 27-21 41-47 50 C26 104 5 90 5 63 V20 Z"
                  fill="rgba(11,12,15,.5)"
                  stroke="var(--cn-sable)"
                  strokeWidth="2"
                />
                <path
                  d="M52 13 L89 26 V62 c0 21-16 32-37 39 C31 94 15 83 15 62 V26 Z"
                  fill="none"
                  stroke="rgba(200,162,74,.3)"
                  strokeWidth="1"
                />
              </svg>
              <div className={styles.shieldPct}>
                <span className={styles.shieldNum}>{percent}</span>
                <span className={`etiq ${styles.shieldCaption}`}>Pour cent</span>
              </div>
            </div>
            <div className={styles.headCopy}>
              {ribbon && <div className="etiq-sable">{ribbon}</div>}
              <h1 id="chapter-end-heading" className={`titre ${styles.rankTitle}`}>{rankTitle}</h1>
              {(pedaEndStats || allySummary.trusted || allySummary.waiting) && (
              <p className={styles.sous}>
                {pedaEndStats
                  ? `${earnedTotal} ${shieldNoun(earnedTotal)} sur ${possibleTotal} possibles dans ce chapitre.`
                  : null}
                {allySummary.trusted && (
                  <>
                    {' '}
                    <span className={styles.sousGold}>{allySummary.trusted}</span>
                  </>
                )}
                {allySummary.waiting && (
                  <>
                    {' '}
                    <span className={styles.sousWait}>{allySummary.waiting}</span>
                  </>
                )}
              </p>
              )}
            </div>
          </div>

          <div className={styles.block}>
            <div className={styles.sect}>
              <span className="etiq">Fiche de vigilance</span>
              <span className={styles.sectFill} />
              <span className={`mono ${styles.sectNote}`}>6 catégories</span>
            </div>
            <div className={styles.tiles}>
              {tiles.map((row) => {
                const { status, caption } = resolvePedaTileStatus(row.earned, row.max);
                const empty = status === 'empty';
                const fill = row.max > 0 ? Math.min(100, (row.earned / row.max) * 100) : 0;
                return (
                  <div
                    key={row.key}
                    className={`${empty ? styles.tileEmpty : 'verre'} ${styles.tile}`}
                  >
                    <div className={styles.tileName}>{row.shortLabel}</div>
                    <div className={styles.tileValues}>
                      <b>{empty ? '—' : row.earned}</b>
                      {!empty && <i>/ {row.max}</i>}
                      <u>{caption}</u>
                    </div>
                    <div className={styles.gauge}>
                      {!empty && <i style={{ width: `${fill}%` }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {showAllyTrust && (
            <div className={styles.block}>
              <div className={styles.sect}>
                <span className="etiq">Ceux qui comptent sur vous</span>
                <span className={styles.sectFill} />
              </div>
              <div className={styles.medals}>
                {visibleAllyTrustRows.map((ally) => (
                  <div
                    key={ally.characterId}
                    className={`verre ${styles.medal} ${ally.points >= 3 ? styles.medalHigh : ally.points === 2 ? styles.medalWarm : ''}`}
                  >
                    <div className={styles.medalRing}>
                      {ally.portraitImage && (
                        <img src={ally.portraitImage} alt="" />
                      )}
                    </div>
                    <div className={styles.medalName}>{ally.givenName}</div>
                    {ally.role && (
                      <div className={`mono ${styles.medalRole}`}>{ally.role}</div>
                    )}
                    <div className={styles.crans} aria-hidden>
                      {[1, 2, 3].map((n) => (
                        <i key={n} className={n <= ally.cranTier ? styles.cranOn : undefined} />
                      ))}
                    </div>
                    <div className={styles.medalDit}>{ally.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={`${styles.lessonCol} cn-zoom-ui`}>
          <aside className={`verre ${styles.lesson}`}>
            <div className={styles.lessonHead}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
                <path
                  d="M12 3 L20 6.5 V12 c0 5-3.4 7.6-8 9-4.6-1.4-8-4-8-9 V6.5 Z"
                  stroke="var(--cn-sable-clair)"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12 l2.2 2.2 L15.5 10"
                  stroke="var(--cn-sable-clair)"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="etiq-sable">Ce qui vous reste</span>
            </div>

            {data.keyIdea && <div className={styles.idea}>{data.keyIdea}</div>}

            {data.reflexes.length > 0 && (
              <div className={styles.refs}>
                <div className="etiq">{data.reflexes.length} réflexes</div>
                {data.reflexes.map((reflex, i) => (
                  <div key={i} className={styles.ref}>
                    <span className={`mono ${styles.refNum}`}>{i + 1}</span>
                    <span className={styles.refText}>
                      <FormattedInlineText text={reflex} />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {showPerfRows && (
              <div className={styles.faits}>
                <div className="etiq">Ce que vous avez fait</div>
                {visiblePerfRows.map((row, i) => {
                  const failed = row.variant !== 'trust' && !row.condition;
                  return (
                    <div
                      key={`${row.label}-${i}`}
                      className={`${styles.fait} ${failed ? styles.faitRate : ''}`}
                    >
                      <span>{row.label}</span>
                      {row.text}
                    </div>
                  );
                })}
              </div>
            )}
          </aside>

          <div className={styles.foot}>
            {onRestart && (
              <button type="button" className={`btn-ghost ${styles.footBtn}`} onClick={onRestart}>
                Rejouer
              </button>
            )}
            {onMenu && (
              <button type="button" className={`btn-ghost ${styles.footBtn}`} onClick={onMenu}>
                Menu
              </button>
            )}
            <button type="button" className={`btn-sable ${styles.continueBtn}`} onClick={onContinue}>
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
